/**
 * Teacher notes on a test result (v3.95.0) — High Frequency Words and CVC
 * Words. Kemal's decisions, 2026-10-30: one optional note per result, dated
 * with the test, written on the test page while or after assessing and
 * editable later from Results; teacher-only — never printed for families (v3.98.0: only on a staff copy of the Student Report),
 * exported, or sent to NextPath or the AI report-card writer.
 *
 * Stored as a `note` string on the result doc itself (hfw_assessments /
 * cvc_assessments), and only when there is one: a result saved without a
 * note has no `note` field. Where "never leaves" is enforced:
 *   - print: every note control and preview on screen is `.no-print`; the
 *     Student Report (v3.98.0) prints notes only on a staff copy, when the
 *     teacher ticks "Include teacher notes" (each printed note then starts
 *     "Staff copy:");
 *   - export: the CSV export writes a fixed field list (src/utils/csv.js
 *     ASSESSMENT_EXPORT_FIELDS) that has no `note`;
 *   - NextPath: the feed maps named fields only (functions/src/nextpath/extract.ts);
 *   - AI: functions/src/ai/deidentify.ts drops `note` from every result.
 * Rules: no change. Anyone who can read the result can read its note (the
 * class's staff, and the school and district leaders who can read scores);
 * the screens that show it: the HFW / CVC Results pages and, since v3.97.0,
 * the Dashboard's notes peek (read-only, screen-only).
 */

/** Longest note, in characters (the textarea's maxLength; also enforced on save). */
export const RESULT_NOTE_MAX = 500;

/** The note on a result, or '' — tolerant of a missing or non-string field. */
export function resultNoteOf(result) {
  return result && typeof result.note === 'string' ? result.note.trim() : '';
}

/** What gets saved: trimmed, at most RESULT_NOTE_MAX characters. */
export function cleanResultNote(text) {
  return typeof text === 'string' ? text.trim().slice(0, RESULT_NOTE_MAX) : '';
}

/** A short one-line preview of a note for a table row. */
export function resultNotePreview(note, max = 80) {
  const oneLine = String(note || '').replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max - 1).trimEnd()}…` : oneLine;
}

/**
 * Whether to offer the signed-in user an editable note — following the
 * Firestore update rule (firestore.rules `canWriteAssessment`): an admin; or,
 * while the student is still in the result's class (`assessmentStudentMatches`
 * — a class move re-points Letter/Sound results only, so an older HFW / CVC
 * result can be left behind), the person who gave the test or the teacher who
 * owns the class (`assessmentTeacherMatchesWriter`). A result without a
 * `teacherId` is editable by the class owner only (the rule errors on the
 * missing field). Not re-checked here: class membership (Results lists only
 * the user's own classes) and `tenantMatchesClass` (a school / district id
 * drifting between the result and its class — rare; the save is then denied
 * with the neutral message). Everyone else sees the note read-only. The rules
 * stay the authority; this only decides which control to show.
 */
export function canEditResultNote(result, user, classDoc, studentDoc) {
  if (!result || !user) return false;
  if (user.role && user.role.admin) return true;
  if (!studentDoc || !result.classId || studentDoc.classId !== result.classId) return false;
  if (result.teacherId && result.teacherId === user.uid) return true;
  return !!(user.role && user.role.teacher && classDoc && classDoc.teacherId === user.uid);
}

/** A copy of the result carrying the saved note ('' → no `note` field), for the page's state after a save. */
export function withResultNote(result, note) {
  const { note: _old, ...rest } = result || {};
  void _old;
  return note ? { ...rest, note } : rest;
}
